import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	ArrowRightOutline,
	Button,
	ChatAlt2Outline,
	ChatOutline,
	Flex,
	HStack,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	rem,
	Text,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';

import { useHomepageRedesignEnabled } from '../BonusFeed/hooks/useHomepageRedesignEnabled';

interface ManagerFeedbackProps {
	isOpen: boolean;
	onClose: VoidFunction;
}

export const ManagerFeedbackModal: FC<ManagerFeedbackProps> = ({ onClose, isOpen }) => {
	const { t } = useTranslation('recognition');
	const dispatch = useDispatch();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	const onClickGiveFeedback = () => {
		onClose();
		dispatch(FeedbackEvent.OpenUnsolicited);
	};

	const onClickAskForFeedback = () => {
		onClose();
		dispatch(FeedbackEvent.OpenRequest);
	};

	if (isHomepageRedesignEnabled) {
		return (
			<Modal autoFocus={false} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size={{ base: 'full', md: 'xl' }}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader px={{ base: rem(16), md: rem(24) }} pt={rem(20)} pb={0} borderBottom={0}>
						<HStack justify="space-between" w="100%">
							<Text color="text.default" fontSize="xl" fontWeight="semibold" lineHeight={8}>
								{t('manager_feedback.title')}
							</Text>
							<ModalCloseButton h={rem(32)} w={rem(32)} pos="static" borderRadius="full" />
						</HStack>
					</ModalHeader>
					<VStack spacing={2} p={{ base: rem(16), md: rem(24) }}>
						<FeedbackOptionButton icon={<ChatOutline color="text.default" />} text={t('manager_feedback.give_feedback')} onClick={onClickGiveFeedback} />
						<FeedbackOptionButton
							icon={<ChatAlt2Outline color="text.default" />}
							text={t('manager_feedback.request_feedback')}
							onClick={onClickAskForFeedback}
						/>
					</VStack>
				</ModalContent>
			</Modal>
		);
	}

	return (
		<Modal autoFocus={false} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="2xl">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader borderBottomWidth={rem(1)} borderColor="border.default" borderStyle="solid" color="text.default">
					{t('manager_feedback.title')}
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />
				<Flex flexDirection="row" alignItems="stretch" align="stretch" alignContent="stretch" gap={2} mx={8} my={3}>
					<FeedbackOptionButton text={t('manager_feedback.give_feedback')} onClick={onClickGiveFeedback} />
					<FeedbackOptionButton text={t('manager_feedback.request_feedback')} onClick={onClickAskForFeedback} />
				</Flex>
			</ModalContent>
		</Modal>
	);
};

const FeedbackOptionButton = ({ icon, text, onClick }: { icon?: React.ReactNode; text: string; onClick: VoidFunction }) => {
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const hoverStyle = {
		_hover: {
			bg: 'hashtag.bgAlphaHover',
		},
	};

	if (isHomepageRedesignEnabled) {
		return (
			<Button variant="alpha" colorScheme="secondary" w="full" borderWidth={rem(1)} borderColor="border.default" height={rem(60)} onClick={onClick}>
				<HStack spacing={rem(12)} w="full" alignItems="flex-start">
					{icon}
					<Text fontSize={100} fontWeight="medium">
						{text}
					</Text>
				</HStack>
				<ArrowRightOutline color="text.default" size="sm" />
			</Button>
		);
	}

	return (
		<Button
			bgColor="hashtag.bgAlpha"
			borderRadius="sm"
			variant="alpha"
			textColor="black"
			width="50%"
			height="140px"
			fontWeight="medium"
			fontSize={100}
			onClick={onClick}
			whiteSpace="normal"
			{...hoverStyle}
		>
			{text}
		</Button>
	);
};
